import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, TrendingUp, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { StatCard, SectionTitle } from "@/components/CaseCard";
import { Skeleton } from "@/components/ui/skeleton";

const COLORS = ["#3A5A40", "#BC5A38", "#2E4835", "#A64E30", "#6E6E64"];

export default function Reports() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const params = {};
  if (from) params.date_from = from;
  if (to) params.date_to = to;

  const { data, isLoading } = useQuery({
    queryKey: ["reports", from, to],
    queryFn: async () => (await api.get("/reports", { params })).data,
  });

  const download = async () => {
    const res = await api.get("/reports/export", { params, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = "compass_report.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-patria-ink">Reports & analytics</h1>
          <p className="text-patria-muted mt-1">Case outcomes, timelines, and campus activity.</p>
        </div>
        <Button onClick={download} data-testid="reports-export" className="rounded-full bg-patria-green hover:bg-patria-greenDeep text-patria-surface">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-patria-surface border border-patria-muted/15 p-3">
        <span className="text-sm text-patria-muted">Date range</span>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="reports-from" className="w-auto bg-patria-surface border-patria-muted/30" />
        <span className="text-patria-muted">→</span>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="reports-to" className="w-auto bg-patria-surface border-patria-muted/30" />
        {(from || to) && <Button variant="ghost" size="sm" onClick={() => { setFrom(""); setTo(""); }} className="text-patria-muted" data-testid="reports-clear">Clear</Button>}
      </div>

      {isLoading || !data ? (
        <div className="grid gap-4 sm:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : (
        <>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
            <StatCard label="Total cases" value={data.total} tone="neutral" testId="report-total" />
            <StatCard label="Active" value={data.active} tone="green" testId="report-active" />
            <StatCard label="Closed" value={data.closed} tone="neutral" testId="report-closed" />
            <StatCard label="Success rate" value={`${data.success_rate}%`} icon={CheckCircle2} tone="green" testId="report-success" />
            <StatCard label="Overdue" value={data.overdue} icon={AlertTriangle} tone={data.overdue ? "red" : "neutral"} testId="report-overdue" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Card className="bg-patria-surface border-patria-muted/15 p-5">
              <div className="flex items-center gap-2 text-patria-muted text-sm"><Clock className="h-4 w-4" /> Avg. time to completion</div>
              <p className="font-heading text-3xl font-semibold text-patria-ink mt-2">{data.avg_time_to_completion_days} <span className="text-lg text-patria-muted">days</span></p>
            </Card>
            <Card className="bg-patria-surface border-patria-muted/15 p-5">
              <div className="flex items-center gap-2 text-patria-muted text-sm"><TrendingUp className="h-4 w-4" /> Outcomes</div>
              <div className="flex flex-wrap gap-4 mt-3">
                {data.by_outcome.length === 0 ? <p className="text-sm text-patria-muted">No closed cases yet.</p> :
                  data.by_outcome.map((o) => (
                    <div key={o.outcome}>
                      <p className="font-heading text-2xl font-semibold text-patria-ink">{o.count}</p>
                      <p className="text-xs text-patria-muted capitalize">{o.outcome.replace("_", " ")}</p>
                    </div>
                  ))}
              </div>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="bg-patria-surface border-patria-muted/15 p-5">
              <SectionTitle>Cases by campus</SectionTitle>
              {data.by_campus.length === 0 ? <p className="text-sm text-patria-muted">No data.</p> : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.by_campus} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <XAxis dataKey="campus" tick={{ fontSize: 10, fill: "#6E6E64" }} interval={0} angle={-15} textAnchor="end" height={50} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6E6E64" }} />
                    <Tooltip contentStyle={{ background: "#FBFAF4", border: "1px solid #6E6E6433", borderRadius: 12 }} />
                    <Bar dataKey="count" fill="#3A5A40" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
            <Card className="bg-patria-surface border-patria-muted/15 p-5">
              <SectionTitle>Cases by stage</SectionTitle>
              {data.by_stage.length === 0 ? <p className="text-sm text-patria-muted">No data.</p> : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={data.by_stage} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={90} label={(e) => e.label}>
                      {data.by_stage.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#FBFAF4", border: "1px solid #6E6E6433", borderRadius: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          <Card className="bg-patria-surface border-patria-muted/15 p-5">
            <SectionTitle>Cases by manager</SectionTitle>
            <div className="space-y-2">
              {data.by_manager.map((m) => (
                <div key={m.manager} className="flex items-center justify-between text-sm border-b border-patria-muted/10 pb-2 last:border-0">
                  <span className="text-patria-ink">{m.manager}</span>
                  <span className="font-medium text-patria-greenDeep">{m.count}</span>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
